import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { MentionSentiment } from 'src/interfaces/topics.interface'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { ReviewAnalysisApiService } from 'src/services/reviewAnalysisApi'
import { ReviewTopicsScopeParams } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'

const scopeKey = (scope: ReviewTopicsScopeParams) => [
  scope.facilityId ?? '',
  scope.platform ?? '',
  scope.venueName ?? '',
  scope.range?.from ?? '',
  scope.range?.to ?? '',
]

// The Topics & Subtopics Overview card for a review campaign.
export const useGetReviewTopicsAnalysisOverviewQuery = (
  campaignId: string,
  scope: ReviewTopicsScopeParams = {}
) =>
  useQuery({
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
    queryFn: () =>
      ReviewAnalysisApiService.getTopicsAnalysisOverview(campaignId, scope),
    queryKey: [
      QueryKey.GET_REVIEW_TOPICS_ANALYSIS_OVERVIEW,
      campaignId,
      ...scopeKey(scope),
    ],
  })

// The Topics & Subtopics table for a review campaign. Scope (facility,
// platform, venue, date range) is part of the key so every filter refetches.
export const useGetReviewTopicsAnalysisQuery = (
  campaignId: string,
  scope: ReviewTopicsScopeParams = {}
) =>
  useQuery({
    enabled: !!campaignId,
    placeholderData: keepPreviousData,
    queryFn: () =>
      ReviewAnalysisApiService.getTopicsAnalysisTable(campaignId, scope),
    queryKey: [
      QueryKey.GET_REVIEW_TOPICS_ANALYSIS,
      campaignId,
      ...scopeKey(scope),
    ],
  })

// One topic, or one subtopic of it, for the modal. Waits for a topic: the
// modal mounts with no selection.
export const useGetReviewTopicAnalysisDetailQuery = (
  campaignId: string,
  topic: string | null,
  subtopic: string | null,
  scope: ReviewTopicsScopeParams = {},
  // The Related Reviews tab: narrows the reviews list alone.
  sentiment: MentionSentiment | null = null
) =>
  useQuery({
    enabled: !!campaignId && !!topic,
    // Switching tab or drilling down keeps the modal filled instead of
    // flashing its skeleton; the modal hides what does not match its view.
    placeholderData: keepPreviousData,
    queryFn: () =>
      ReviewAnalysisApiService.getTopicsAnalysisDetail(
        campaignId,
        topic as string,
        subtopic,
        scope,
        sentiment
      ),
    queryKey: [
      QueryKey.GET_REVIEW_TOPIC_ANALYSIS_DETAIL,
      campaignId,
      topic,
      subtopic ?? null,
      sentiment ?? null,
      ...scopeKey(scope),
    ],
  })
